import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { familyProfiles } from '../data/familyProfiles.js';
import { ActivityLogger } from '../lib/activityLogger.js';

export default function FamilyMembersShowcase() {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  const getStoryForProfile = (profile) => {
    const stories = {
      'FATHER': 'Founded our family legacy with wisdom and dedication. A teacher who shaped generations.',
      'MOTHER': 'Heart of our family, nurturing bonds and preserving memories across generations.',
      'ELDER SON': 'Carries forward the family vision with innovation and business acumen.',
      'YOUNGER SON': 'Growing into leadership, bridging tradition and modernity.',
      '2ND YOUNGER SON': 'Young spirit, the future of our legacy and family dreams.'
    };
    return stories[profile.familyRole] || `An important part of our family's legacy`;
  };

  const handleProfileClick = (profile) => {
    ActivityLogger.featureAccess('family_member_profile', {
      member_name: profile.name,
      member_role: profile.familyRole
    });
    navigate(`/profile/${profile.id}`);
    // Scroll to top of profile page
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const displayProfiles = familyProfiles.slice(0, 5);
  const parentProfiles = displayProfiles.slice(0, 2); // First 2: Father, Mother
  const brotherProfiles = displayProfiles.slice(2, 5); // Last 3: Brothers

  return (
    <section style={{ marginTop: 64, marginBottom: 64, padding: '0 20px' }}>
      {/* Header */}
      <div style={{ marginBottom: 48, textAlign: 'left' }}>
        <h2 style={{ 
          color: '#c9a62b', 
          fontSize: 36, 
          marginBottom: 12,
          fontWeight: '600',
          letterSpacing: '-0.5px'
        }}>
          Family Members
        </h2>
        <p style={{ 
          color: '#999', 
          fontSize: 15,
          margin: 0,
          fontWeight: '400',
          lineHeight: 1.5
        }}>
          The heart of our legacy — each member brings their unique story and contributes to our family's narrative
        </p>
      </div>

      {/* Parents Row - 2 columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 0,
        marginBottom: 64,
        position: 'relative',
        maxWidth: '100%'
      }}>
        {parentProfiles.map((profile, index) => {
          const isHovered = hoveredId === profile.id;

          return (
            <div key={profile.id} style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              {/* Card Container */}
              <div style={{
                paddingRight: index < parentProfiles.length - 1 ? 16 : 0,
                paddingLeft: 16,
                width: '100%',
                maxWidth: 320
              }}>
                {/* Vertical Divider - between parent cards */}
                {index < parentProfiles.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    right: 8,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: 'linear-gradient(to bottom, transparent 0%, rgba(201,166,43,0.3) 15%, rgba(201,166,43,0.3) 85%, transparent 100%)',
                    pointerEvents: 'none'
                  }}></div>
                )}

              {/* Card */}
            <div
              onClick={() => handleProfileClick(profile)}
              onMouseEnter={() => setHoveredId(profile.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                paddingTop: 0,
                paddingBottom: 32,
                borderBottom: `1px solid ${isHovered ? 'rgba(201,166,43,0.4)' : 'rgba(201,166,43,0.15)'}`,
                transition: 'border-color 0.3s ease'
              }}
            >
              {/* Photo/Image Section */}
              <div style={{
                width: '100%',
                height: 200,
                borderRadius: 8,
                overflow: 'hidden',
                marginBottom: 24,
                border: `1px solid ${isHovered ? 'rgba(201,166,43,0.3)' : 'rgba(201,166,43,0.1)'}`,
                opacity: isHovered ? 0.95 : 0.8,
                transition: 'all 0.3s ease',
                perspective: '1000px'
              }}>
                <img 
                  src={profile.photo} 
                  alt={profile.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 25%',
                    transform: isHovered ? 'rotateY(15deg) rotateX(-8deg)' : 'rotateY(0deg) rotateX(0deg)',
                    transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.320, 1)',
                    transformStyle: 'preserve-3d'
                  }}
                  onError={(e) => {
                    e.target.style.background = 'rgba(201,166,43,0.1)';
                  }}
                />
              </div>

              {/* Card Content */}
              <div>
                {/* Title and Description */}
                <h3 style={{
                  color: '#fff',
                  margin: '0 0 8px 0',
                  fontSize: 18,
                  fontWeight: '600',
                  letterSpacing: '-0.3px'
                }}>
                  {profile.name}
                </h3>

                <p style={{
                  color: '#999',
                  margin: '0 0 20px 0',
                  fontSize: 14,
                  lineHeight: 1.6,
                  fontWeight: '400'
                }}>
                  {getStoryForProfile(profile)}
                </p>

                {/* Info Grid */}
                <div style={{
                  display: 'grid',
                  gap: 12,
                  marginBottom: 20,
                  paddingBottom: 20,
                  borderBottom: `1px solid ${isHovered ? 'rgba(201,166,43,0.2)' : 'rgba(201,166,43,0.1)'}`
                }}>
                  {profile.familyRole && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13
                    }}>
                      <span style={{ color: '#666' }}>Role</span>
                      <span style={{ color: '#c9a62b', fontWeight: '500' }}>
                        {profile.familyRole}
                      </span>
                    </div>
                  )}

                  {profile.dob && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13
                    }}>
                      <span style={{ color: '#666' }}>Born</span>
                      <span style={{ color: '#ccc' }}>
                        {profile.dob}
                      </span>
                    </div>
                  )}

                  {profile.occupation && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13
                    }}>
                      <span style={{ color: '#666' }}>Occupation</span>
                      <span style={{ color: '#ccc' }}>
                        {profile.occupation}
                      </span>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#c9a62b',
                  fontSize: 13,
                  fontWeight: '500',
                  opacity: isHovered ? 1 : 0.6,
                  transition: 'opacity 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <span>View Profile</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>
            </div>
            </div>
            </div>
          );
        })}
      </div>

      {/* Brothers Row - 3 columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 0,
        marginBottom: 48,
        position: 'relative',
        maxWidth: '100%'
      }}>
        {brotherProfiles.map((profile, index) => {
          const isHovered = hoveredId === profile.id;

          return (
            <div key={profile.id} style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              {/* Card Container */}
              <div style={{
                paddingRight: index < brotherProfiles.length - 1 ? 16 : 0,
                paddingLeft: 16,
                width: '100%',
                maxWidth: 320
              }}>
                {/* Vertical Divider - between brother cards */}
                {index < brotherProfiles.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    right: 16,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: 'linear-gradient(to bottom, transparent 0%, rgba(201,166,43,0.3) 15%, rgba(201,166,43,0.3) 85%, transparent 100%)',
                    pointerEvents: 'none'
                  }}></div>
                )}

              {/* Card */}
            <div
              onClick={() => handleProfileClick(profile)}
              onMouseEnter={() => setHoveredId(profile.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                paddingTop: 0,
                paddingBottom: 32,
                borderBottom: `1px solid ${isHovered ? 'rgba(201,166,43,0.4)' : 'rgba(201,166,43,0.15)'}`,
                transition: 'border-color 0.3s ease'
              }}
            >
              {/* Photo/Image Section */}
              <div style={{
                width: '100%',
                height: 200,
                borderRadius: 8,
                overflow: 'hidden',
                marginBottom: 24,
                border: `1px solid ${isHovered ? 'rgba(201,166,43,0.3)' : 'rgba(201,166,43,0.1)'}`,
                opacity: isHovered ? 0.95 : 0.8,
                transition: 'all 0.3s ease',
                perspective: '1000px'
              }}>
                <img 
                  src={profile.photo} 
                  alt={profile.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 25%',
                    transform: isHovered ? 'rotateY(15deg) rotateX(-8deg)' : 'rotateY(0deg) rotateX(0deg)',
                    transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.320, 1)',
                    transformStyle: 'preserve-3d'
                  }}
                  onError={(e) => {
                    e.target.style.background = 'rgba(201,166,43,0.1)';
                  }}
                />
              </div>

              {/* Card Content */}
              <div>
                {/* Title and Description */}
                <h3 style={{
                  color: '#fff',
                  margin: '0 0 8px 0',
                  fontSize: 18,
                  fontWeight: '600',
                  letterSpacing: '-0.3px'
                }}>
                  {profile.name}
                </h3>

                <p style={{
                  color: '#999',
                  margin: '0 0 20px 0',
                  fontSize: 14,
                  lineHeight: 1.6,
                  fontWeight: '400'
                }}>
                  {getStoryForProfile(profile)}
                </p>

                {/* Info Grid */}
                <div style={{
                  display: 'grid',
                  gap: 12,
                  marginBottom: 20,
                  paddingBottom: 20,
                  borderBottom: `1px solid ${isHovered ? 'rgba(201,166,43,0.2)' : 'rgba(201,166,43,0.1)'}`
                }}>
                  {profile.familyRole && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13
                    }}>
                      <span style={{ color: '#666' }}>Role</span>
                      <span style={{ color: '#c9a62b', fontWeight: '500' }}>
                        {profile.familyRole}
                      </span>
                    </div>
                  )}

                  {profile.dob && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13
                    }}>
                      <span style={{ color: '#666' }}>Born</span>
                      <span style={{ color: '#ccc' }}>
                        {profile.dob}
                      </span>
                    </div>
                  )}

                  {profile.occupation && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13
                    }}>
                      <span style={{ color: '#666' }}>Occupation</span>
                      <span style={{ color: '#ccc' }}>
                        {profile.occupation}
                      </span>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#c9a62b',
                  fontSize: 13,
                  fontWeight: '500',
                  opacity: isHovered ? 1 : 0.6,
                  transition: 'opacity 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <span>View Profile</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>
            </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
